import { useMemo, useState } from 'react';
import { useQuery } from 'wasp/client/operations';
import { getDiscussions } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { routes } from 'wasp/client/router';
import { DiscussionCard } from './DiscussionCard';
import { FilterPill } from '../components/common';
import {
  LanguageSelect,
  LinkButton,
  SearchField,
  SortSelect,
} from '../components/ui';
import { FilterSidebar, FilterSection } from '../components/layout';
import { Helmet } from 'react-helmet-async';
import {
  DISCUSSION_LANGUAGE_FILTER_OPTIONS,
  isDiscussionLanguageFilter,
} from './languageOptions';
import type { DiscussionLanguageFilter } from './types';
import { useDebounce } from '../shared/hooks/useDebounce';

type SortOption = 'newest' | 'oldest' | 'mostViewed';

export function DiscussionsPage() {
  const { data: user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [language, setLanguage] = useState<DiscussionLanguageFilter>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const normalizedSearchQuery = debouncedSearchQuery.trim();
  const normalizedTags = useMemo(
    () => [...selectedTags].sort(),
    [selectedTags],
  );

  const {
    data: discussions,
    isLoading,
    error,
  } = useQuery(
    getDiscussions,
    {
      language: language === 'all' ? undefined : language,
      sortBy,
      tags: normalizedTags,
      searchQuery:
        normalizedSearchQuery === '' ? undefined : normalizedSearchQuery,
    },
    {
      placeholderData: (previousData: unknown) => previousData,
    },
  );

  // DiscussionsPage:availableTags-merge — Merges selectedTags into availableTags
  // to preserve selected tag visibility even when filtered out of query results
  const availableTags = useMemo(() => {
    const tagsFromDiscussions = discussions
      ? discussions.flatMap((discussion) => discussion.tags)
      : [];
    return [...new Set([...selectedTags, ...tagsFromDiscussions])].sort();
  }, [discussions, selectedTags]);

  const sortOptions: { label: string; value: SortOption }[] = [
    { label: 'Newest First', value: 'newest' },
    { label: 'Oldest First', value: 'oldest' },
    { label: 'Most Viewed', value: 'mostViewed' },
  ];

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleReset = () => {
    setLanguage('all');
    setSortBy('newest');
    setSelectedTags([]);
  };

  if (error)
    return (
      <div className="text-center mt-8 text-danger-500">
        Error: {error.message}
      </div>
    );

  const sidebarFilters = (
    <>
      <FilterSection heading="Language">
        <LanguageSelect
          value={language}
          onChange={(value) => {
            if (isDiscussionLanguageFilter(value)) {
              setLanguage(value);
            }
          }}
          options={DISCUSSION_LANGUAGE_FILTER_OPTIONS}
          aria-label="Filter by language"
        />
      </FilterSection>

      <FilterSection heading="Sort">
        <SortSelect
          value={sortBy}
          onChange={(value) => setSortBy(value as SortOption)}
          options={sortOptions}
          aria-label="Sort discussions"
        />
      </FilterSection>

      {availableTags.length > 0 && (
        <FilterSection heading="Tags">
          <div className="flex flex-wrap gap-1.5">
            {availableTags.map((tag) => (
              <FilterPill
                key={tag}
                label={tag}
                isSelected={selectedTags.includes(tag)}
                onClick={() => toggleTag(tag)}
              />
            ))}
          </div>
        </FilterSection>
      )}
    </>
  );

  const hasActiveFilters =
    searchQuery !== '' ||
    selectedTags.length > 0 ||
    language !== 'all' ||
    sortBy !== 'newest';
  const isEmpty = !isLoading && discussions?.length === 0;

  const discussionContent = (
    <>
      {isLoading && !discussions ? (
        <div className="grid gap-6">
          <p
            className="text-sm text-neutral-500 dark:text-neutral-400"
            role="status"
            aria-live="polite"
          >
            Loading discussions...
          </p>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-lg border border-neutral-200 dark:border-strokedark bg-white dark:bg-boxdark p-6"
            >
              <div className="h-5 w-2/3 rounded bg-neutral-200 dark:bg-neutral-700 mb-4" />
              <div className="h-4 w-full rounded bg-neutral-200 dark:bg-neutral-700 mb-2" />
              <div className="h-4 w-4/5 rounded bg-neutral-200 dark:bg-neutral-700 mb-4" />
              <div className="flex gap-2">
                <div className="h-6 w-16 rounded-full bg-neutral-200 dark:bg-neutral-700" />
                <div className="h-6 w-16 rounded-full bg-neutral-200 dark:bg-neutral-700" />
              </div>
            </div>
          ))}
        </div>
      ) : isEmpty && hasActiveFilters ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="text-5xl mb-4">💬</div>
          <h3 className="text-lg font-semibold text-neutral-700 dark:text-neutral-200 mb-2">
            No discussions found
          </h3>
          <p className="text-neutral-500 dark:text-neutral-400">
            Try adjusting your filters or search query.
          </p>
        </div>
      ) : (
        <div className="mx-auto max-w-3xl grid gap-6">
          {discussions?.map((discussion) => (
            <DiscussionCard key={discussion.id} discussion={discussion} />
          ))}
        </div>
      )}
    </>
  );

  const emptyState = (
    <div className="flex flex-col items-center justify-center text-center py-16 px-4">
      <div className="text-5xl mb-4">💬</div>
      <h3 className="text-lg font-semibold text-neutral-700 dark:text-neutral-200 mb-2">
        No discussions yet
      </h3>
      <p className="text-neutral-500 dark:text-neutral-400">
        Be the first to start a conversation!
      </p>
    </div>
  );

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://optimistic-news.com/discussions" />
        <title>Discussions - Optimistic News</title>
        <meta
          name="description"
          content="Join the discussion about positive news and uplifting stories on Optimistic News."
        />
      </Helmet>
      <div className="py-6 lg:py-10">
        <FilterSidebar
          sidebarLabel="Filters"
          onReset={handleReset}
          content={
            <>
              {/* Search + CTA centered above cards */}
              <div className="mx-auto mb-6 flex max-w-2xl flex-col gap-3 sm:flex-row sm:items-center">
                <SearchField
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search discussions..."
                  className="flex-1"
                />
                {user && (
                  <LinkButton
                    to={routes.CreateDiscussionRoute.to}
                    className="shrink-0 px-5 py-2"
                  >
                    Start Discussion
                  </LinkButton>
                )}
              </div>
              {isEmpty && !hasActiveFilters ? emptyState : discussionContent}
            </>
          }
        >
          {sidebarFilters}
        </FilterSidebar>
      </div>
    </>
  );
}
